import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { isAuthenticated } from './lib/auth';

import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/HomeResponsive';
import DigiConnect from './pages/DigiConnect';
import DigiMeet from './pages/DigiMeet';
import BrowseUsers from './pages/BrowseUsers';
import UserProfile from './pages/UserProfile';
import ProfileEdit from './pages/ProfileEdit';
import Settings from './pages/Settings';
import ScheduleMeeting from './pages/ScheduleMeeting';
import MeetingConfirmed from './pages/MeetingConfirmed';
import MeetingReschedule from './pages/MeetingReschedule';
import MeetingCancel from './pages/MeetingCancel';
import MeetRoom from './pages/MeetRoom';
import Community from './pages/CommunityNew';
import Chat from './pages/Chat';
import MyMeetings from './pages/MyMeetings';
import DigiCalendar from './pages/DigiCalendar';
import GoogleOAuthCallback from './pages/GoogleOAuthCallback';
import DigiSlidesPro from './pages/DigiSlidesPro';
import DigiSlidesCanvaEditor from './pages/DigiSlidesEnhanced';
import DigiBranding from './pages/DigiBranding';
import DigiBrandingEditor from './pages/DigiBrandingEditor';
import HRPayroll from './pages/HRPayroll';
import EventbriteClone from './pages/EventbriteClone';
import EventDetails from './pages/EventDetails';
import DigiCRM from './pages/DigiCRM';
import WebsiteBuilder from './pages/WebsiteBuilderEnhanced';
import PublishedSite from './pages/PublishedSite';
import Pricing from './pages/Pricing';
import SubscriptionDashboard from './pages/SubscriptionDashboard';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancelled from './pages/SubscriptionCancelled';
import Strategy from './pages/Strategy';
import Presentor from './pages/PresentorNew';
import DigiStrategy from './pages/DigiStrategyWithEditor';
import DigiEditor from './pages/DigiEditor';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import About from './pages/About';
import AIChatbot from './components/AIChatbot';
import Contacts from './pages/Contacts';
import DigiConnectLanding from './pages/DigiConnectLanding';
import DigiMeetLanding from './pages/DigiMeetLanding';
import AllAppsLanding from './pages/AllAppsLanding';
import DigiCRMLanding from './pages/DigiCRMLanding';
import DigiCommunityLanding from './pages/DigiCommunityLanding';
import DigiWebsiteLanding from './pages/DigiWebsiteLanding';
import DigiPresentorLanding from './pages/DigiPresentorLanding';
import DigiCalendarLanding from './pages/DigiCalendarLanding';
import ContactGroups from './pages/ContactGroups';
import ContactGroupForm from './pages/ContactGroupForm';
import WebScraper from './pages/WebScraper';
import J1Visa from './pages/J1Visa';
import CalendlyIntegration from './pages/CalendlyIntegration';
import ReviewManager from './pages/ReviewManager';
import ReviewLanding from './pages/ReviewLanding';
import CouponManager from './pages/CouponManager';
import AutoWebsiteBuilder from './pages/AutoWebsiteBuilder';
import QuickWebsiteBuilder from './pages/QuickWebsiteBuilder';
import AutoWebsiteView from './pages/AutoWebsiteView';
import WebsiteBuilderChoice from './pages/WebsiteBuilderChoice';
import GroupView from './pages/GroupView';
import PhotoVideoEvents from './pages/PhotoVideoEvents';
import PhotoVideoEventForm from './pages/PhotoVideoEventForm';
import MediaEventView from './pages/MediaEventView';
import AdminNFCWriter from './pages/AdminNFCWriter';
import EventTypes from './pages/EventTypes';
import CommunityVideos from './pages/CommunityVideos';
import EventTypeForm from './pages/EventTypeForm';
import PublicBooking from './pages/PublicBooking';
import EventTypeAnalytics from './pages/EventTypeAnalytics';
import DigiSquad from './pages/DigiSquad';
import DigiPosterCreator from './pages/DigiPosterCreator';
import DigiHostess from './pages/DigiHostess';
import PhotoboothAccess from './photobooth/PhotoboothAccess';
import PhotoboothDashboard from './photobooth/PhotoboothDashboard';
import PhotoboothUpload from './photobooth/PhotoboothUpload';
import PhotoboothEditor from './photobooth/PhotoboothEditor';
import ContactGroupsAccess from './components/ContactGroupsAccess';
import HRDashboard from './pages/HRDashboard';
import EmployeeManagement from './pages/EmployeeManagement';
import LeaveManagement from './pages/LeaveManagement';
import AttendanceView from './pages/AttendanceView';

const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/auth" replace />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Home />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/about" element={<About />} />
          
          {/* Landing Pages */}
          <Route path="/all-apps" element={<AllAppsLanding />} />
          <Route path="/landing/digi-connect" element={<DigiConnectLanding />} />
          <Route path="/landing/digi-meet" element={<DigiMeetLanding />} />
          <Route path="/landing/digi-crm" element={<DigiCRMLanding />} />
          <Route path="/landing/digi-community" element={<DigiCommunityLanding />} />
          <Route path="/landing/digi-website" element={<DigiWebsiteLanding />} />
          <Route path="/landing/digi-presentor" element={<DigiPresentorLanding />} />
          <Route path="/landing/digi-calendar" element={<DigiCalendarLanding />} />
          <Route path="/contacts" element={
            <ProtectedRoute>
              <Contacts />
            </ProtectedRoute>
          } />

          <Route path="/contact-groups" element={
            <ProtectedRoute>
              <ContactGroupsAccess>
                <ContactGroups />
              </ContactGroupsAccess>
            </ProtectedRoute>
          } />

          <Route path="/contact-groups/create" element={
            <ProtectedRoute>
              <ContactGroupsAccess>
                <ContactGroupForm />
              </ContactGroupsAccess>
            </ProtectedRoute>
          } />

          <Route path="/web-scraper" element={
            <ProtectedRoute>
              <WebScraper />
            </ProtectedRoute>
          } />

          <Route path="/j1-visa" element={<J1Visa />} />
          
          <Route path="/calendly-integration" element={<CalendlyIntegration />} />
          
          <Route path="/review-manager" element={
            <ProtectedRoute>
              <ReviewManager />
            </ProtectedRoute>
          } />
          
          <Route path="/coupon-manager" element={
            <ProtectedRoute>
              <CouponManager />
            </ProtectedRoute>
          } />
          
          <Route path="/review/:businessId" element={<ReviewLanding />} />

          <Route path="/contact-groups/edit/:groupId" element={
            <ProtectedRoute>
              <ContactGroupsAccess>
                <ContactGroupForm />
              </ContactGroupsAccess>
            </ProtectedRoute>
          } />

          {/* Public route for QR code scanning */}
          <Route path="/group/:groupId" element={<GroupView />} />

          <Route path="/photo-video-events" element={
            <ProtectedRoute>
              <PhotoVideoEvents />
            </ProtectedRoute>
          } />

          <Route path="/photo-video-events/create" element={
            <ProtectedRoute>
              <PhotoVideoEventForm />
            </ProtectedRoute>
          } />

          <Route path="/photo-video-events/edit/:eventId" element={
            <ProtectedRoute>
              <PhotoVideoEventForm />
            </ProtectedRoute>
          } />

          {/* Public route for media event viewing/uploading */}
          <Route path="/media-event/:eventId" element={<MediaEventView />} />

          {/* Admin NFC Writer */}
          <Route path="/admin/nfc-writer" element={
            <ProtectedRoute>
              <AdminNFCWriter />
            </ProtectedRoute>
          } />
          
          <Route path="/digi-connect" element={
            <ProtectedRoute>
              <DigiConnect />
            </ProtectedRoute>
          } />
          
          <Route path="/digi-hostess" element={
            <ProtectedRoute>
              <DigiHostess />
            </ProtectedRoute>
          } />
          
          <Route path="/digi-meet" element={<DigiMeet />} />
          
          <Route path="/browse" element={
            <ProtectedRoute>
              <BrowseUsers />
            </ProtectedRoute>
          } />
          
          <Route path="/profile/:userId" element={<UserProfile />} />
          
          <Route path="/profile/edit" element={
            <ProtectedRoute>
              <ProfileEdit />
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          
          <Route path="/schedule/:hostId" element={<ScheduleMeeting />} />
          <Route path="/meeting/confirmed/:meetingId" element={<MeetingConfirmed />} />
          <Route path="/meeting/reschedule/:meetingId" element={<MeetingReschedule />} />
          <Route path="/meeting/cancel/:meetingId" element={<MeetingCancel />} />
          {/* Public route - no authentication required for meeting room */}
          <Route path="/meet/:roomId" element={<MeetRoom />} />
          
          <Route path="/community" element={
            <ProtectedRoute>
              <Community />
            </ProtectedRoute>
          } />
          
          <Route path="/my-meetings" element={
            <ProtectedRoute>
              <MyMeetings />
            </ProtectedRoute>
          } />
          
          <Route path="/digi-calendar" element={<DigiCalendar />} />
          <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
          <Route path="/digi-slides" element={<DigiSlidesPro />} />
          <Route path="/digi-slides/editor" element={<DigiSlidesCanvaEditor />} />
          
          <Route path="/event-types" element={
            <ProtectedRoute>
              <EventTypes />
            </ProtectedRoute>
          } />
          
          <Route path="/event-types/new" element={
            <ProtectedRoute>
              <EventTypeForm />
            </ProtectedRoute>
          } />
          
          <Route path="/event-types/:eventTypeId/edit" element={
            <ProtectedRoute>
              <EventTypeForm />
            </ProtectedRoute>
          } />
          
          <Route path="/event-types/:eventTypeId/analytics" element={
            <ProtectedRoute>
              <EventTypeAnalytics />
            </ProtectedRoute>
          } />
          
          <Route path="/book/:userId/:slug" element={<PublicBooking />} />
          
          <Route 
            path="/ai-team" 
            element={
              <ProtectedRoute>
                <DigiSquad />
              </ProtectedRoute>
            } 
          />
          
          
          <Route path="/digi-branding" element={
            <ProtectedRoute>
              <DigiBranding />
            </ProtectedRoute>
          } />

          <Route path="/digi-branding/editor" element={
            <ProtectedRoute>
              <DigiBrandingEditor />
            </ProtectedRoute>
          } />

          <Route path="/community-videos" element={<CommunityVideos />} />

          {/* Business Tools Routes */}
          <Route path="/hr-payroll" element={
            <ProtectedRoute>
              <HRPayroll />
            </ProtectedRoute>
          } />

          <Route path="/events" element={<EventbriteClone />} />
          
          <Route path="/events/:eventId" element={<EventDetails />} />

          <Route path="/crm-leads" element={<DigiCRM />} />
          <Route path="/digi-crm" element={<DigiCRM />} />
          <Route path="/digi-squad" element={<DigiSquad />} />
          <Route path="/digi-poster" element={<DigiPosterCreator />} />
          
          {/* Website Builder Choice Page */}
          <Route path="/website-choice" element={
            <ProtectedRoute>
              <WebsiteBuilderChoice />
            </ProtectedRoute>
          } />

          {/* Advanced Website Builder (keeps all previous data) */}
          <Route path="/website-builder" element={<WebsiteBuilder />} />

          {/* Quick Auto Website Generator (new) */}
          <Route path="/auto-website" element={<QuickWebsiteBuilder />} />
          <Route path="/auto-website-builder" element={<QuickWebsiteBuilder />} />
          
          {/* Public auto-generated website view */}
          <Route path="/auto-site/:websiteId" element={<AutoWebsiteView />} />

          <Route path="/strategy" element={
            <ProtectedRoute>
              <Strategy />
            </ProtectedRoute>
          } />

          <Route path="/presentor" element={<Presentor />} />

          {/* Digi Strategy Routes */}
          <Route path="/digi-strategy" element={
            <ProtectedRoute>
              <DigiStrategy />
            </ProtectedRoute>
          } />

          {/* Digi Editor Route */}
          <Route path="/digi-editor" element={
            <ProtectedRoute>
              <DigiEditor />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          
          <Route path="/chat/:otherUserId" element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          } />

          {/* Subscription Routes */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/subscription" element={
            <ProtectedRoute>
              <SubscriptionDashboard />
            </ProtectedRoute>
          } />
          <Route path="/subscription/success" element={
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          } />
          <Route path="/subscription/cancelled" element={
            <ProtectedRoute>
              <SubscriptionCancelled />
            </ProtectedRoute>
          } />

          {/* Photobooth Routes - Access restricted to potraitchocolate@gmail.com */}
          <Route path="/photobooth" element={
            <ProtectedRoute>
              <PhotoboothAccess>
                <PhotoboothDashboard />
              </PhotoboothAccess>
            </ProtectedRoute>
          } />
          
          <Route path="/photobooth/upload/:templateId" element={<PhotoboothUpload />} />
          
          <Route path="/photobooth/editor/:templateId" element={
            <ProtectedRoute>
              <PhotoboothAccess>
                <PhotoboothEditor />
              </PhotoboothAccess>
            </ProtectedRoute>
          } />

          {/* HR Ops Routes - No Authentication Required */}
          <Route path="/hr-ops" element={<HRDashboard />} />
          <Route path="/hr-ops/employees" element={<EmployeeManagement />} />
          <Route path="/hr-ops/leaves" element={<LeaveManagement />} />
          <Route path="/hr-ops/attendance" element={<AttendanceView />} />

          {/* Published Sites - Must be at the end to avoid route conflicts */}
          <Route path="/sites/:subdomain" element={<PublishedSite />} />
        </Routes>
        
        {/* AI Chatbot - Available on all pages */}
        <AIChatbot />
      </BrowserRouter>
    </div>
  );
}

export default App;
